import { useEffect, useMemo, useState } from "react";
import { Layers, Clock, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDmcaSettings, useDmcaSummary, useUpdateDmcaSettings } from "@/hooks/useDmcaData";
import {
  DEFAULT_DMCA_SETTINGS,
  DMCA_SETTINGS_BOUNDS,
  type DmcaSettings as DmcaSettingsValues,
  type TierMetric,
} from "@/types/dmca";

// Keys that hold a number (everything except the tier_metric enum).
type NumericKey = keyof typeof DMCA_SETTINGS_BOUNDS;

// Bright Data SERP cost per request (one domain check = one request).
const COST_PER_REQUEST = 0.0015;
// Flag a plan whose projected spend jumps past this — catches fat-finger
// frequencies (e.g. hourly Tier 1 ≈ $1,170/mo instead of $49).
const COST_WARN_MONTHLY = 150;

// Human-readable check frequencies ↔ interval hours. Covers the DB range
// (1..8760). An interval not in this list renders as a one-off "custom" option.
const FREQUENCIES: { label: string; hours: number }[] = [
  { label: "Hourly", hours: 1 },
  { label: "Every 6 hours", hours: 6 },
  { label: "Twice a day", hours: 12 },
  { label: "Once a day", hours: 24 },
  { label: "Every 2 days", hours: 48 },
  { label: "Twice a week", hours: 84 },
  { label: "Once a week", hours: 168 },
  { label: "Every 2 weeks", hours: 336 },
  { label: "Monthly", hours: 720 },
];
/**
 * DMCA + Indexation tiering & schedule settings (the dmca_settings row). Admins
 * only — the Settings route is admin-gated and the table's RLS enforces it again.
 * Tiering: tier 1 = top N domains by the chosen metric over the window; tier 2 =
 * the rest with any of that metric; tier 3 (no metric) is not tracked. Schedule:
 * how often tiers 1 and 2 are re-checked. One check reads both DMCA and index.
 */

const NUMERIC_KEYS = Object.keys(DMCA_SETTINGS_BOUNDS) as NumericKey[];

const clamp = (k: NumericKey, v: number) => {
  const { min, max } = DMCA_SETTINGS_BOUNDS[k];
  if (Number.isNaN(v)) return min;
  return Math.min(max, Math.max(min, Math.round(v)));
};

function NumberField({
  id,
  label,
  suffix,
  value,
  onChange,
  min,
  max,
}: {
  id: NumericKey;
  label: string;
  suffix?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          value={Number.isNaN(value) ? "" : value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-40"
        />
        {suffix ? <span className="text-sm text-muted-foreground">{suffix}</span> : null}
      </div>
    </div>
  );
}

function FrequencySelect({ hours, onChange }: { hours: number; onChange: (h: number) => void }) {
  const known = FREQUENCIES.some((f) => f.hours === hours);
  const opts = known ? FREQUENCIES : [{ label: `Every ${hours}h (custom)`, hours }, ...FREQUENCIES];
  return (
    <Select value={String(hours)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-8 w-[168px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {opts.map((f) => (
          <SelectItem key={f.hours} value={String(f.hours)}>
            {f.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function MonitoringSettings() {
  const { data: settings, isPending } = useDmcaSettings();
  const { data: summary } = useDmcaSummary();
  const update = useUpdateDmcaSettings();

  // All domains = tracked (summary.domains is `where active`) + deliberately
  // untracked tier-3. After migration 20261021100000 `domains` is tracked-only,
  // so add untracked back for the "top X% of all domains" denominator. Pre-
  // migration untracked is absent → 0, so this still equals the full count.
  const totalDomains = (summary?.domains ?? 0) + (summary?.domains_untracked ?? 0);
  const metricWord = (form: DmcaSettingsValues) => (form.tier_metric === "revenue" ? "revenue" : "clicks");

  const [form, setForm] = useState<DmcaSettingsValues>(DEFAULT_DMCA_SETTINGS);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [notAdmin, setNotAdmin] = useState(false);

  // Sync the form when the settings load (or change elsewhere).
  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const dirty = useMemo(
    () => (settings ? (Object.keys(form) as (keyof DmcaSettingsValues)[]).some((k) => form[k] !== settings[k]) : false),
    [form, settings],
  );

  const touched = () => {
    setSavedAt(null);
    setNotAdmin(false);
  };
  const set = (k: NumericKey, v: number) => {
    setForm((f) => ({ ...f, [k]: v }));
    touched();
  };
  const setMetric = (m: TierMetric) => {
    setForm((f) => ({ ...f, tier_metric: m }));
    touched();
  };

  // Tier 1 as a % of all domains, kept in sync with the absolute tier1_limit.
  const tier1Pct = totalDomains > 0 ? Math.round((form.tier1_limit / totalDomains) * 100) : 0;
  const setTier1Pct = (p: number) => {
    const n = totalDomains > 0 ? Math.round((p / 100) * totalDomains) : form.tier1_limit;
    set("tier1_limit", clamp("tier1_limit", n));
  };

  // Per-tier counts + estimated Bright Data spend, from the live summary (the
  // current nightly assignment — a changed tier-1 size takes effect on the next
  // rebuild, per the note below, so the plan shows what is actually tiered now).
  // URL targets exist per published URL and inherit their domain's tier. Tier 3
  // (no metric) and inactive targets are excluded, so they cost nothing. Each
  // tier = domains × (24 / interval) checks/day × $/request — domain checks only;
  // a flagged domain adds per-URL stage-2 checks we can't predict here.
  const cost = useMemo(() => {
    const t1 = summary?.domains_tier1 ?? 0;
    const t2 = summary?.domains_tier2 ?? 0;
    const u1 = summary?.urls_tier1 ?? 0;
    const u2 = summary?.urls_tier2 ?? 0;
    const exclD = (summary?.domains_tier3 ?? 0) + (summary?.domains_untracked ?? 0);
    const exclU = (summary?.urls_tier3 ?? 0) + (summary?.urls_untracked ?? 0);
    const perDay = (count: number, hours: number) =>
      hours > 0 ? count * (24 / hours) * COST_PER_REQUEST : 0;
    const d1 = perDay(t1, form.tier1_interval_hours);
    const d2 = perDay(t2, form.tier2_interval_hours);
    return { t1, t2, u1, u2, exclD, exclU, d1, d2, dayTotal: d1 + d2 };
  }, [summary, form.tier1_interval_hours, form.tier2_interval_hours]);

  const usd = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const usd0 = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const monthlyTotal = cost.dayTotal * 30;

  const onSave = () => {
    // Clamp the numeric keys to their DB bounds; keep tier_metric as-is. Tier 3
    // interval stays whatever is stored (no control for it) — it isn't tracked.
    const patch: DmcaSettingsValues = { ...form };
    for (const k of NUMERIC_KEYS) patch[k] = clamp(k, form[k]);
    setForm(patch);
    update.mutate(patch, {
      onSuccess: (rows) => {
        if (rows > 0) setSavedAt(Date.now());
        else setNotAdmin(true); // RLS updated nothing → caller isn't admin
      },
    });
  };

  if (isPending) {
    return <p className="text-sm text-muted-foreground">Loading settings…</p>;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        One Google-check engine powers both DMCA takedown detection and index
        status — these tiers and intervals govern both features.
      </p>

      {/* Tiering */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="size-4" /> Tiering
          </CardTitle>
          <CardDescription>
            Which domains land in each tier. Tier 1 = the top domains by the
            chosen metric; tier 2 = the rest with any of that metric; tier 3 (no
            metric) is not tracked.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="max-w-xl space-y-1.5">
            <Label htmlFor="tier_metric">Tier by</Label>
            <Select value={form.tier_metric} onValueChange={(v) => setMetric(v as TierMetric)}>
              <SelectTrigger id="tier_metric" className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="clicks">Human clicks</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Ranking key. <strong>Human clicks</strong> = clean traffic — Keitaro
              clicks with our own bot traffic excluded. <strong>Revenue</strong> =
              money earned over the window.
            </p>
          </div>
          <div className="max-w-xl space-y-2">
            <Label htmlFor="tier1_pct">Tier 1 size — top domains by {metricWord(form)}</Label>
            <div className="flex items-center gap-3">
              <input
                id="tier1_pct"
                type="range"
                min={1}
                max={100}
                value={tier1Pct}
                onChange={(e) => setTier1Pct(Number(e.target.value))}
                className="h-2 flex-1 cursor-pointer accent-primary"
              />
              <span className="w-10 text-right text-sm font-medium tabular-nums">{tier1Pct}%</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span>
                Top {tier1Pct}% ≈{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {form.tier1_limit.toLocaleString()}
                </span>{" "}
                of {totalDomains.toLocaleString()} domains
              </span>
              <span className="flex items-center gap-1.5">
                · exact
                <Input
                  type="number"
                  value={Number.isNaN(form.tier1_limit) ? "" : form.tier1_limit}
                  onChange={(e) => set("tier1_limit", Number(e.target.value))}
                  min={DMCA_SETTINGS_BOUNDS.tier1_limit.min}
                  max={DMCA_SETTINGS_BOUNDS.tier1_limit.max}
                  className="h-8 w-28"
                />
              </span>
            </div>
          </div>
          <div className="space-y-1.5">
            <NumberField
              id="clicks_window_days"
              label="Metric window"
              suffix="days"
              value={form.clicks_window_days}
              onChange={(v) => set("clicks_window_days", v)}
              min={DMCA_SETTINGS_BOUNDS.clicks_window_days.min}
              max={DMCA_SETTINGS_BOUNDS.clicks_window_days.max}
            />
            <p className="text-xs text-muted-foreground">
              Days of {metricWord(form)} used to rank domains — and the period the
              money columns sum over on the Indexation page.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Schedule — per-tier plan with live cost */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="size-4" /> Check schedule
          </CardTitle>
          <CardDescription>
            How often each tier is re-checked. One check reads both DMCA and index
            status · {`$${COST_PER_REQUEST.toFixed(4)}`} per check.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Per-tier plan */}
          <div className="space-y-1 text-sm">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t py-2">
              <span className="size-2 shrink-0 rounded-full bg-primary" />
              <span className="w-16 font-medium">Tier 1</span>
              <span className="min-w-[160px] flex-1 tabular-nums text-muted-foreground">
                {cost.t1.toLocaleString()} domains · {cost.u1.toLocaleString()} URLs
              </span>
              <FrequencySelect hours={form.tier1_interval_hours} onChange={(h) => set("tier1_interval_hours", h)} />
              <span className="ml-auto whitespace-nowrap tabular-nums text-muted-foreground">{usd0(cost.d1 * 30)}/mo</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t py-2">
              <span className="size-2 shrink-0 rounded-full bg-sky-500" />
              <span className="w-16 font-medium">Tier 2</span>
              <span className="min-w-[160px] flex-1 tabular-nums text-muted-foreground">
                {cost.t2.toLocaleString()} domains · {cost.u2.toLocaleString()} URLs
              </span>
              <FrequencySelect hours={form.tier2_interval_hours} onChange={(h) => set("tier2_interval_hours", h)} />
              <span className="ml-auto whitespace-nowrap tabular-nums text-muted-foreground">{usd0(cost.d2 * 30)}/mo</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t py-2">
              <span className="size-2 shrink-0 rounded-full border-2 border-muted-foreground/60" />
              <span className="w-16 font-medium">Tier 3</span>
              <span className="min-w-[160px] flex-1 tabular-nums text-muted-foreground">
                {cost.exclD.toLocaleString()} domains · {cost.exclU.toLocaleString()} URLs
              </span>
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Excluded — not tracked
              </span>
              <span className="ml-auto whitespace-nowrap tabular-nums text-muted-foreground">—</span>
            </div>
            <div className="flex items-center justify-between border-t-2 pt-2 font-medium">
              <span className="tabular-nums">
                Tracked: {(cost.t1 + cost.t2).toLocaleString()} domains · {(cost.u1 + cost.u2).toLocaleString()} URLs
              </span>
              <span className="tabular-nums">
                {usd(cost.dayTotal)}/day · {usd0(monthlyTotal)}/mo
              </span>
            </div>
          </div>

          {monthlyTotal > COST_WARN_MONTHLY ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                This plan is ≈ {usd0(monthlyTotal)}/mo — well above the usual ~$69. Double-check
                the frequencies before saving.
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* When changes take effect — from the dmca_settings table contract. */}
      <p className="text-xs text-muted-foreground">
        A changed frequency applies from each target's next check. A changed tier-1
        size, metric or window applies on the next nightly rebuild (01:30 UTC).
        Cost is domain checks only — a flagged domain adds per-URL checks this can't
        predict.
      </p>

      <div className="flex items-center gap-3">
        <Button onClick={onSave} disabled={!dirty || update.isPending} className="cursor-pointer">
          {update.isPending ? "Saving…" : "Save changes"}
        </Button>
        {savedAt ? (
          <span className="inline-flex items-center gap-1 text-sm text-[var(--chart-2)]">
            <Check className="size-4" /> Saved
          </span>
        ) : null}
        {notAdmin ? (
          <span className="text-sm text-destructive">Admin role required to change these.</span>
        ) : null}
        {update.isError ? (
          <span className="text-sm text-destructive">Could not save — try again.</span>
        ) : null}
      </div>
    </div>
  );
}
